import { Link } from 'react-router';
import { useAccueilTresorerie } from '@/api/hooks';
import { LIBELLE_STATUT_DOSSIER, type AccueilCaissier, type AccueilResponsable, type AccueilTresorier, type StatutDossier } from '@/api/types';
import { Chargement, ErreurBloc, Kpi, PageHeader } from '@/components/common';
import { CarteCompte, OngletsTresorerie, TableDossiers, libelleSemaine } from '@/components/tresorerie';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, montant } from '@/lib/utils';

const ETAPES: StatutDossier[] = ['VALIDE', 'CONTROLE', 'RETOURNE', 'TRANSMIS', 'PREPARATION', 'POSTE', 'A_CONFIRMER', 'SUSPENDU'];

function Responsable({ a }: { a: AccueilResponsable }) {
  return (
    <div className="space-y-6">
      {a.alertes.length > 0 && (
        <div className="space-y-2">
          {a.alertes.map((x, i) => (
            <Alert key={i} variant={x.niveau === 'critique' ? 'destructive' : 'default'} className={cn(x.niveau === 'alerte' && 'border-amber-300 bg-amber-50')}>
              <AlertDescription>{x.message}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Disponible (tous comptes)" valeur={montant(a.totalDisponible)} detail={`prévisionnel fin de semaine ${montant(a.totalPrevisionnel)}`} accent={a.totalPrevisionnel < 0 ? 'critique' : undefined} />
        <Kpi label="Décaissements programmés" valeur={montant(a.decaissementsSemaine)} detail={libelleSemaine(a.semaine)} />
        <Kpi label="Payés cette semaine" valeur={a.payesCetteSemaine.nombre} detail={montant(a.payesCetteSemaine.montant)} accent="ok" />
        <Kpi label="Opérations à signer" valeur={a.operationsEnAttente} detail="retours de caisse et nivellements" accent={a.operationsEnAttente > 0 ? 'alerte' : undefined} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>FED en trésorerie par statut</CardTitle>
          <CardDescription>Cliquez un statut pour ouvrir la liste correspondante.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {ETAPES.map((s) => (
            <Link key={s} to={`/tresorerie/dossiers?statut=${s}`} className={cn('flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-accent/40', s === 'SUSPENDU' && (a.compteurs[s] ?? 0) > 0 && 'border-red-300')}>
              <span>{LIBELLE_STATUT_DOSSIER[s]}</span>
              <span className="num font-semibold">{a.compteurs[s] ?? 0}</span>
            </Link>
          ))}
        </CardContent>
      </Card>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {a.comptes.map((c) => (
          <CarteCompte key={c.id} c={c} lien={`/tresorerie/dossiers?compteId=${c.id}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/tresorerie/situation">{a.situationDuJourTransmise ? 'Situation du jour (transmise)' : 'Transmettre la situation du jour'}</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/tresorerie/plan">Plan de décaissement</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/tresorerie/operations">Retours et nivellements</Link>
        </Button>
      </div>
    </div>
  );
}

function Tresorier({ a }: { a: AccueilTresorier }) {
  return (
    <div className="space-y-6">
      {a.operationsAExecuter > 0 && (
        <Alert>
          <AlertTitle>{a.operationsAExecuter} nivellement(s) validé(s) à exécuter</AlertTitle>
          <AlertDescription>
            <Link to="/tresorerie/operations" className="underline">
              Ouvrir les opérations
            </Link>
          </AlertDescription>
        </Alert>
      )}
      {a.comptes.length === 0 && (
        <Alert>
          <AlertTitle>Aucun compte ne vous est affecté</AlertTitle>
          <AlertDescription>Le responsable de la trésorerie vous affecte à un ou plusieurs comptes bancaires du Fonds.</AlertDescription>
        </Alert>
      )}
      {a.comptes.map(({ compte, aPreparer, enAttenteValidateurs, aConfirmer, payesRecents }) => (
        <Card key={compte.id}>
          <CardHeader>
            <CardTitle>
              {compte.banque} · {compte.code}
            </CardTitle>
            <CardDescription>
              Disponible {montant(compte.soldeDisponible, true)} · postés en attente {montant(compte.postesEnAttente, true)} · prévisionnel {montant(compte.soldePrevisionnel, true)}
              {compte.sousMinimum && <span className="ml-2 font-medium text-red-700">sous le minimum</span>}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <section>
              <h3 className="mb-2 text-sm font-semibold">À préparer et à poster ({aPreparer.length})</h3>
              <TableDossiers dossiers={aPreparer} vide="Aucun paiement à préparer sur ce compte." colonnes={['statut', 'semaine', 'delai']} />
            </section>
            <section>
              <h3 className="mb-2 text-sm font-semibold">Postés, en attente des validateurs ({enAttenteValidateurs.length})</h3>
              <TableDossiers dossiers={enAttenteValidateurs} vide="Aucun virement en attente de validation." colonnes={['statut', 'delai']} />
            </section>
            <section>
              <h3 className="mb-2 text-sm font-semibold">Validés en banque, à confirmer ({aConfirmer.length})</h3>
              <TableDossiers dossiers={aConfirmer} vide="Aucun paiement à confirmer." colonnes={['statut', 'delai']} />
            </section>
            {payesRecents.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold">Payés récemment</h3>
                <TableDossiers dossiers={payesRecents} colonnes={['statut', 'paiement']} />
              </section>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Caissier({ a }: { a: AccueilCaissier }) {
  return (
    <div className="space-y-6">
      {a.alertes.map((x, i) => (
        <Alert key={i} variant="destructive">
          <AlertDescription>{x}</AlertDescription>
        </Alert>
      ))}
      <div className="grid gap-3 sm:grid-cols-3">
        <Kpi label="Solde de caisse disponible" valeur={montant(a.caisse?.soldeDisponible ?? 0, true)} detail={a.caisse?.soldeMaximum !== null && a.caisse?.soldeMaximum !== undefined ? `maximum ${montant(a.caisse.soldeMaximum)}` : undefined} accent={(a.caisse?.soldeDisponible ?? 0) < 0 ? 'critique' : undefined} />
        <Kpi label="Paiements à préparer" valeur={a.paiements.aPreparer.length + a.paiements.enPreparation.length} accent={a.paiements.aPreparer.length > 0 ? 'alerte' : undefined} />
        <Kpi label="Arrêté du jour" valeur={a.arreteDuJour ? 'établi' : 'à faire'} detail={a.arreteDuJour ? `écart ${montant(a.arreteDuJour.ecart, true)}` : 'en fin de journée'} accent={a.arreteDuJour ? 'ok' : undefined} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Paiements en espèces ({a.paiements.aPreparer.length + a.paiements.enPreparation.length})</CardTitle>
          <CardDescription>Bon de caisse, identité du bénéficiaire et reçu signé pour chaque paiement.</CardDescription>
        </CardHeader>
        <CardContent>
          <TableDossiers dossiers={[...a.paiements.aPreparer, ...a.paiements.enPreparation]} vide="Aucun paiement en espèces en attente." colonnes={['statut', 'semaine', 'delai']} />
        </CardContent>
      </Card>
      <div className="flex gap-2">
        <Button asChild>
          <Link to="/tresorerie/caisse">Caisse et arrêté du jour</Link>
        </Button>
      </div>
    </div>
  );
}

/** Accueil de la trésorerie selon le rôle (RT-1 responsable, TR-1 trésorier, CA-1 caissier). */
export function AccueilTresorerie() {
  const { data, isLoading, error } = useAccueilTresorerie();
  return (
    <div>
      <PageHeader titre="Trésorerie" sousTitre={data?.role === 'RESPONSABLE' ? 'Pilotage des décaissements, des comptes du Fonds et de la caisse.' : data?.role === 'TRESORIER' ? 'Mes paiements par compte bancaire.' : data?.role === 'CAISSIER' ? 'Paiements en espèces et arrêté de caisse.' : undefined} />
      <OngletsTresorerie />
      {isLoading ? <Chargement /> : error || !data ? <ErreurBloc erreur={error} /> : data.role === 'RESPONSABLE' ? <Responsable a={data} /> : data.role === 'TRESORIER' ? <Tresorier a={data} /> : <Caissier a={data} />}
    </div>
  );
}
